import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { cn } from "../lib/utils";

// Each category gets richer metadata so the card can feel alive on hover.
const CATEGORIES = [
  {
    id: "acne",
    label: "Acne & Retinoids",
    sub: "Isotretinoin, tretinoin, adapalene",
    img: "/categories/acne.png",
    group: "skin",
    meds: ["Accutane", "Tretinoin", "Adapalene"],
    dominant: "RED",
    note: "Avoid stacking exfoliants",
  },
  {
    id: "rosacea",
    label: "Rosacea",
    sub: "Metronidazole, ivermectin, brimonidine",
    img: "/categories/rosacea.png",
    group: "skin",
    meds: ["Metronidazole", "Ivermectin", "Brimonidine"],
    dominant: "YELLOW",
    note: "Skip alcohol & strong fragrance",
  },
  {
    id: "antibiotics",
    label: "Antibiotics",
    sub: "Doxycycline, minocycline, clindamycin",
    img: "/categories/antibiotics.png",
    group: "med",
    meds: ["Doxycycline", "Minocycline", "Bactrim"],
    dominant: "YELLOW",
    note: "Photosensitivity — daily SPF",
  },
  {
    id: "eczema",
    label: "Eczema & Barrier",
    sub: "Topical steroids, calcineurin inhibitors",
    img: "/categories/eczema.png",
    group: "skin",
    meds: ["Hydrocortisone", "Tacrolimus", "Pimecrolimus"],
    dominant: "GREEN",
    note: "Pause acids on inflamed skin",
  },
  {
    id: "photo",
    label: "Photosensitivity",
    sub: "Doxycycline, hydrochlorothiazide, NSAIDs",
    img: "/categories/photo.png",
    group: "med",
    meds: ["Doxycycline", "HCTZ", "Methotrexate"],
    dominant: "RED",
    note: "Daily broad-spectrum SPF",
  },
  {
    id: "hormonal",
    label: "Hormonal & PCOS",
    sub: "Spironolactone, contraceptives, HRT",
    img: "/categories/hormonal.png",
    group: "med",
    meds: ["Spironolactone", "Combined OCP", "Tamoxifen"],
    dominant: "GREEN",
    note: "Check each active with your med",
  },
  {
    id: "hyperpigmentation",
    label: "Hyperpigmentation",
    sub: "Hydroquinone, kojic acid, azelaic acid",
    img: "/categories/hyperpigmentation.png",
    group: "skin",
    meds: ["Hydroquinone 4%", "Tri-Luma", "Azelaic 15%"],
    dominant: "YELLOW",
    note: "Mandatory daily SPF",
  },
  {
    id: "antiaging",
    label: "Anti-aging",
    sub: "Retinol, peptides, growth factors",
    img: "/categories/antiaging.png",
    group: "skin",
    meds: ["Tretinoin", "Tazarotene", "Adapalene"],
    dominant: "YELLOW",
    note: "Don't stack retinoids",
  },
  {
    id: "pregnancy",
    label: "Pregnancy & nursing",
    sub: "Common pregnancy & nursing questions",
    img: "/categories/pregnancy.png",
    group: "special",
    meds: ["Azelaic acid", "Niacinamide", "Mineral SPF"],
    dominant: "GREEN",
    note: "Avoid retinoids + hydroquinone",
  },
  {
    id: "sun",
    label: "Sun Protection",
    sub: "Mineral & chemical SPF compatibility",
    img: "/categories/sun.png",
    group: "special",
    meds: ["Avobenzone", "Zinc oxide", "Octinoxate"],
    dominant: "GREEN",
    note: "Your last morning step",
  },
];

const FILTERS = [
  { id: "all", label: "All categories" },
  { id: "skin", label: "Skin conditions" },
  { id: "med", label: "Medication classes" },
  { id: "special", label: "Special situations" },
];

const GROUP_LABEL = {
  skin: "Skin condition",
  med: "Medication class",
  special: "Special situation",
};

// Tapping a category opens the checker pre-filled with a representative
// medication from that category (these meds are shown on the card itself —
// nothing is substituted silently). Values resolve via the checker's med=
// resolver (canonical id or generic-name match). Ingredient-only categories
// (pregnancy, sun) have no single medication, so they open a clean checker.
const CAT_SEED_MED = {
  acne: "isotretinoin",
  rosacea: "metronidazole",
  antibiotics: "doxycycline",
  eczema: "hydrocortisone",
  photo: "doxycycline",
  hormonal: "spironolactone",
  hyperpigmentation: "hydroquinone",
  antiaging: "tretinoin",
};


export default function CategoryShowcase() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [hoverId, setHoverId] = useState(null);
  const [pulseIdx, setPulseIdx] = useState(0);

  const visible = filter === "all"
    ? CATEGORIES
    : CATEGORIES.filter((c) => c.group === filter);

  // Calm "spotlight" cycles through visible cards when nothing is hovered.
  useEffect(() => {
    if (hoverId) return undefined;
    const id = setInterval(() => {
      setPulseIdx((v) => (v + 1) % Math.max(1, visible.length));
    }, 3600);
    return () => clearInterval(id);
  }, [hoverId, visible.length]);

  const spotlightId = hoverId
    ? hoverId
    : visible[pulseIdx % visible.length]?.id;

  return (
    <div data-testid="category-showcase">
      {/* Filter pills */}
      <div className="flex flex-wrap items-center gap-2 mb-7">
        {FILTERS.map((f) => {
          const isActive = filter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              data-testid={`category-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={cn(
                "inline-flex items-center gap-2 px-4 h-9 rounded-full border text-[13px] font-semibold transition-all duration-300",
                isActive
                  ? "bg-ink text-cream-50 border-ink shadow-card -translate-y-0.5"
                  : "bg-white border-surface-line text-ink-soft hover:bg-cream-100 hover:-translate-y-0.5",
              )}
            >
              {f.label}
              <span
                className={cn(
                  "text-[11px] opacity-70 tabular-nums transition-opacity",
                  isActive ? "opacity-100" : "",
                )}
              >
                {f.id === "all"
                  ? CATEGORIES.length
                  : CATEGORIES.filter((c) => c.group === f.id).length}
              </span>
            </button>
          );
        })}
        <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-ink-mute">
          <Sparkles className="w-3 h-3 text-peach-500" />
          Tap a category to explore
        </span>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {visible.map((cat) => (
          <CategoryCard
            key={cat.id}
            cat={cat}
            spotlight={cat.id === spotlightId}
            onHoverStart={() => setHoverId(cat.id)}
            onHoverEnd={() => setHoverId(null)}
            onClick={() => {
              const seed = CAT_SEED_MED[cat.id];
              navigate(seed ? `/check?med=${seed}` : "/check");
            }}
          />
        ))}
      </div>
    </div>
  );
}

function CategoryCard({ cat, spotlight, onHoverStart, onHoverEnd, onClick }) {
  return (
    <button
      type="button"
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      onFocus={onHoverStart}
      onBlur={onHoverEnd}
      onClick={onClick}
      data-testid={`category-tile-${cat.id}`}
      data-spotlight={spotlight ? "true" : "false"}
      className={cn(
        "group relative aspect-[3/4] rounded-2xl overflow-hidden text-left transition-all duration-500 ease-out",
        "border bg-cream-100 focus:outline-none focus:ring-4 focus:ring-peach-100",
        spotlight
          ? "border-transparent shadow-floating -translate-y-1 scale-[1.012]"
          : "border-surface-line shadow-subtle hover:-translate-y-1 hover:shadow-card",
      )}
    >
      {/* Image with subtle parallax / zoom.
          `title=""` is intentional — it prevents browsers (and some image-
          search extensions) from showing a fallback tooltip on hover. */}
      <img
        src={cat.img}
        alt={cat.label}
        title=""
        loading="lazy"
        className={cn(
          "absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] ease-out",
          spotlight ? "scale-[1.08]" : "group-hover:scale-[1.07]",
        )}
      />

      {/* Bottom gradient — deepens on hover/spotlight for legibility */}
      <span
        aria-hidden
        className={cn(
          "absolute inset-x-0 bottom-0 h-3/4 transition-opacity duration-500 pointer-events-none",
          spotlight ? "opacity-100" : "opacity-90 group-hover:opacity-100",
        )}
        style={{
          background:
            "linear-gradient(to top, rgba(20,17,12,0.86) 0%, rgba(20,17,12,0.55) 35%, rgba(20,17,12,0) 75%)",
        }}
      />

      {/* Spotlight glow ring (neutral — not a risk signal) */}
      {spotlight && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{ boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.5)" }}
        />
      )}

      {/* Category-type label — neutral navigation descriptor, never a verdict.
          Fades out when the card expands so it can't collide with the heading
          on narrow screens. */}
      <span
        className={cn(
          "absolute top-3 right-3 inline-flex items-center px-2 py-1 rounded-full backdrop-blur-md text-[10px] font-bold uppercase tracking-wider transition-opacity duration-300",
          spotlight ? "opacity-0" : "opacity-100 group-hover:opacity-0",
        )}
        style={{
          background: "rgba(255,255,255,0.88)",
          color: "#4a4336",
        }}
      >
        {GROUP_LABEL[cat.group] || "Category"}
      </span>

      {/* Label block — always visible at the bottom */}
      <div className="absolute inset-x-0 bottom-0 p-4 text-cream-50">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">
          Category
        </div>
        <div className="font-bold text-[15px] leading-tight mt-1">
          {cat.label}
        </div>
        <div className="text-[12px] opacity-80 mt-1 line-clamp-1">
          {cat.sub}
        </div>

        {/* Slide-up reveal panel on hover/spotlight */}
        <div
          className={cn(
            "mt-3 transition-all duration-500 ease-out",
            spotlight
              ? "opacity-100 translate-y-0 max-h-[120px] sm:max-h-[180px]"
              : "opacity-0 translate-y-3 max-h-0 group-hover:opacity-100 group-hover:translate-y-0 group-hover:max-h-[120px] sm:group-hover:max-h-[180px]",
          )}
        >
          <div className="hidden sm:block text-[10px] uppercase tracking-[0.18em] font-bold opacity-70 mb-1.5">
            Top medications
          </div>
          <div className="flex flex-wrap gap-1">
            {cat.meds.map((m) => (
              <span
                key={m}
                className="text-[10px] px-2 py-0.5 rounded-full bg-white/15 border border-white/25 backdrop-blur-md text-cream-50"
              >
                {m}
              </span>
            ))}
          </div>
          <div className="hidden sm:block mt-2.5 text-[11px] opacity-90 italic">
            {cat.note}
          </div>
          <span className="mt-3 inline-flex items-center gap-1 text-[12px] font-bold text-cream-50 transition-all duration-300">
            Start a check
            <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </button>
  );
}
